import { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useSearchParams } from "react-router-dom";
import {
  LISTING_PATHS,
  SITE_URL,
  conversationContext,
  fetchConversation,
  fetchConversations,
  fetchMessages,
  markRead,
  myIdIn,
  sendMessage,
} from "src/utils/chat";
import { realtime } from "src/utils/realtime";

// Guest conversations, live over the websocket. Contact details are hidden
// by the API in guest–host chats.

function timeLabel(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  return date.toDateString() === new Date().toDateString()
    ? date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : date.toLocaleDateString([], { day: "numeric", month: "short" });
}

const Avatar = ({ person }) =>
  person?.photo ? (
    <img src={person.photo} alt="" style={{ borderRadius: "50%", objectFit: "cover" }} />
  ) : (
    <span className="hd-avatar-initial">{(person?.name || "?").charAt(0).toUpperCase()}</span>
  );

const Messages = () => {
  const [params, setParams] = useSearchParams();
  const selectedId = params.get("conversation") || "";
  const [conversations, setConversations] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [draft, setDraft] = useState("");
  const [otherTyping, setOtherTyping] = useState(false);
  const bottomRef = useRef(null);
  const myIdRef = useRef("");
  const typingSentAt = useRef(0);
  const typingTimer = useRef(null);

  const me = myIdIn(selected);
  myIdRef.current = me;

  useEffect(() => {
    fetchConversations()
      .then((data) => setConversations(data.results))
      .finally(() => setLoadingList(false));
    const release = realtime.retain();
    const off = realtime.on("chat.conversation", ({ conversation }) => {
      setConversations((list) => [conversation, ...list.filter((c) => c.id !== conversation.id)]);
      setSelected((current) => (current?.id === conversation.id ? conversation : current));
    });
    return () => {
      off();
      release();
    };
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setSelected(null);
      return undefined;
    }
    let active = true;
    setLoadingThread(true);
    setMessages([]);
    Promise.all([fetchConversation(selectedId), fetchMessages(selectedId)])
      .then(([conversation, page]) => {
        if (!active) return;
        setSelected(conversation);
        setMessages(page.messages);
        setHasMore(page.hasMore);
        markRead(selectedId).catch(() => {});
        setConversations((list) => list.map((c) => (c.id === selectedId ? { ...c, unread_count: 0 } : c)));
      })
      .catch(() => active && setSelected(null))
      .finally(() => active && setLoadingThread(false));

    const topic = `chat:${selectedId}`;
    const unsubscribe = realtime.subscribe(topic);
    const offMessage = realtime.on("chat.message", ({ message }, eventTopic) => {
      if (eventTopic !== topic) return;
      setMessages((list) => {
        if (list.some((m) => m.id === message.id)) return list;
        const pending = list.findIndex((m) => message.client_id && m.client_id === message.client_id);
        if (pending >= 0) {
          const next = [...list];
          next[pending] = message;
          return next;
        }
        return [...list, message];
      });
      if (message.sender_id !== myIdRef.current) {
        setOtherTyping(false);
        markRead(selectedId).catch(() => {});
      }
    });
    const offTyping = realtime.on("chat.typing", ({ user_id: userId, typing }, eventTopic) => {
      if (eventTopic !== topic || userId === myIdRef.current) return;
      setOtherTyping(typing);
      clearTimeout(typingTimer.current);
      if (typing) typingTimer.current = setTimeout(() => setOtherTyping(false), 6000);
    });
    const offRead = realtime.on("chat.read", ({ reader_id: readerId, read_at: readAt }, eventTopic) => {
      if (eventTopic !== topic || readerId === myIdRef.current) return;
      setMessages((list) => list.map((m) => (m.read_at ? m : { ...m, read_at: readAt })));
    });
    return () => {
      active = false;
      unsubscribe();
      offMessage();
      offTyping();
      offRead();
      setOtherTyping(false);
    };
  }, [selectedId]);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ block: "end" });
  }, [messages.length, selectedId]);

  const open = (id) => setParams(id ? { conversation: id } : {});

  const loadEarlier = async () => {
    if (!messages.length) return;
    const page = await fetchMessages(selectedId, messages[0].id);
    setMessages((list) => [...page.messages, ...list]);
    setHasMore(page.hasMore);
  };

  const deliver = useCallback(async (conversationId, body, clientId) => {
    try {
      const saved = await sendMessage(conversationId, body, clientId);
      setMessages((list) => list.filter((m) => m.id !== saved.id).map((m) => (m.client_id === clientId ? saved : m)));
    } catch (error) {
      setMessages((list) => list.map((m) => (m.client_id === clientId ? { ...m, failed: true } : m)));
    }
  }, []);

  const send = () => {
    const body = draft.trim();
    if (!body || !selected) return;
    const clientId = `host-${Date.now()}`;
    setMessages((list) => [
      ...list,
      {
        id: clientId,
        client_id: clientId,
        conversation_id: selected.id,
        sender_id: me,
        body,
        is_system: false,
        redacted: false,
        created_at: new Date().toISOString(),
        read_at: null,
      },
    ]);
    setDraft("");
    typingSentAt.current = 0;
    realtime.sendTyping(`chat:${selected.id}`, false);
    deliver(selected.id, body, clientId);
  };

  const retry = (message) => {
    setMessages((list) => list.map((m) => (m.client_id === message.client_id ? { ...m, failed: false } : m)));
    deliver(message.conversation_id, message.body, message.client_id);
  };

  const onDraftChange = (value) => {
    setDraft(value);
    if (selectedId && value && Date.now() - typingSentAt.current > 3000) {
      typingSentAt.current = Date.now();
      realtime.sendTyping(`chat:${selectedId}`, true);
    }
  };

  const listingUrl = selected?.listing ? `${SITE_URL}/${LISTING_PATHS[selected.listing.type]}/${selected.listing.id}` : null;

  return (
    <>
      <Helmet>
        <title>Messages | Tubayo Host</title>
      </Helmet>
      <div className="hd-page">
        <div className="hd-page-header">
          <div>
            <h1>Messages</h1>
            <p>Talk to guests before and after they book. Phone numbers and locations stay hidden.</p>
          </div>
        </div>

        <div className={`hd-card hd-messages ${selectedId ? "thread-open" : ""}`}>
          <div className="hd-inbox">
            {loadingList ? (
              [0, 1, 2].map((i) => <div key={i} className="hd-skeleton" />)
            ) : conversations.length === 0 ? (
              <div className="hd-empty">
                <i className="far fa-comment-dots" />
                <h3>No messages yet</h3>
                <p>When guests message you about a listing, the conversation appears here.</p>
              </div>
            ) : (
              conversations.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={`hd-conversation ${c.id === selectedId ? "active" : ""}`}
                  onClick={() => open(c.id)}
                >
                  <Avatar person={c.other_party} />
                  <span className="hd-conversation-text">
                    <strong>
                      {c.other_party.name} <small>{timeLabel(c.last_message_at)}</small>
                    </strong>
                    <span>{conversationContext(c)}</span>
                    <span className={c.unread_count ? "unread" : ""}>{c.last_message_preview}</span>
                  </span>
                  {c.unread_count > 0 && <span className="hd-badge">{c.unread_count}</span>}
                </button>
              ))
            )}
          </div>

          <div className="hd-thread">
            {!selectedId ? (
              <div className="hd-thread-empty">Select a conversation</div>
            ) : loadingThread && !selected ? (
              <div className="hd-thread-empty">Loading…</div>
            ) : !selected ? (
              <div className="hd-thread-empty">This conversation isn't available.</div>
            ) : (
              <>
                <div className="hd-thread-header">
                  <button type="button" className="hd-icon-btn hd-thread-back" onClick={() => open("")} aria-label="Back">
                    <i className="fas fa-arrow-left" />
                  </button>
                  <Avatar person={selected.other_party} />
                  <div>
                    <strong>{selected.other_party.name}</strong>
                    {otherTyping ? (
                      <span className="typing">typing…</span>
                    ) : listingUrl ? (
                      <a href={listingUrl} target="_blank" rel="noopener noreferrer" className="hd-muted">
                        {selected.listing.title}
                      </a>
                    ) : (
                      <span>{conversationContext(selected)}</span>
                    )}
                  </div>
                </div>

                <div className="hd-message-list">
                  {hasMore && (
                    <button type="button" className="hd-btn" style={{ alignSelf: "center" }} onClick={loadEarlier}>
                      Load earlier messages
                    </button>
                  )}
                  {messages.map((m) =>
                    m.is_system ? (
                      <div key={m.id} className="hd-system-message">
                        <i className="far fa-calendar-check" /> {m.body}
                      </div>
                    ) : (
                      <div key={m.id} className={`hd-bubble-row ${m.sender_id === me ? "mine" : ""}`}>
                        <div className="hd-bubble">
                          {m.body}
                          <small>
                            {timeLabel(m.created_at)}
                            {m.sender_id === me && !m.failed && (m.id === m.client_id ? " · Sending…" : m.read_at ? " · Seen" : "")}
                          </small>
                        </div>
                        {m.failed && (
                          <button type="button" className="hd-retry" onClick={() => retry(m)}>
                            Not sent · Tap to retry
                          </button>
                        )}
                        {m.redacted && m.sender_id === me && (
                          <span className="hd-note">
                            Contact details and locations are hidden in Tubayo messages, so bookings and payments
                            stay protected.
                          </span>
                        )}
                      </div>
                    ),
                  )}
                  <div ref={bottomRef} />
                </div>

                <form
                  className="hd-composer"
                  onSubmit={(e) => {
                    e.preventDefault();
                    send();
                  }}
                >
                  <textarea
                    value={draft}
                    rows={1}
                    maxLength={2000}
                    placeholder="Write a message…"
                    aria-label="Message"
                    onChange={(e) => onDraftChange(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        send();
                      }
                    }}
                  />
                  <button type="submit" disabled={!draft.trim()} aria-label="Send">
                    <i className="fas fa-paper-plane" />
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Messages;
